import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../d-me-organizations';
import type { NameEntry } from 'src/store/selectors';
import 'src/library/elements/d-smooth-resize';
import 'src/library/fields/d-view-html';
import 'src/library/fields/d-closer';
import { functionIds } from 'src/store/selectors/functions/function-ids';
import { t } from 'src/i18n';
import { ContactDialog } from 'src/outskirts/account/emergency/contact-dialog';
import type {
  EmergencyFunctionWithTasks,
  EmergencyMeasuresCard,
  EmergencyTask,
} from 'src/outskirts/account/emergency/emergency-view-model';

@customElement('d-emergency-measures-card')
export class DEmergencyMeasuresCard extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    :host > * {
      line-height: normal;
    }
    .functions {
      position: relative;
      display: block;
      margin-bottom: 12px;
      border-radius: 24px;
      background-color: hsl(0, 0%, 84%);
      padding: 10px;
      font-family: var(--main-sans-narrow), sans-serif;
      font-size: 20px;
      font-weight: 500;
    }
    d-closer {
      width: 30px;
      height: 30px;
      opacity: 0;
    }
    .open > d-closer {
      opacity: 0.5;
    }
    @media (hover: hover) {
      .open > d-closer:hover {
        opacity: 1;
      }
    }
    .tasks-header {
      padding: 0 4px;
      font-family: var(--mainSans), sans-serif;
      font-size: 20px;
      font-weight: 500;
    }
    .tasks-header.user-tasks:not(.no-tasks) {
      margin-bottom: -6px;
    }
    .tasks-header.other-tasks {
      cursor: pointer;
    }
    .tasks-header.no-tasks:after {
      content: ' Ingen oppgaver'; /* i18n-exempt: CSS ::after-etikett */
      font-size: 17px;
      font-weight: 200;
    }
    .function {
      padding-top: 6px;
    }
    .function-header {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 4px;
      margin: 0 4px;
      padding-top: 2px;
    }
    .function-name {
      margin-right: 4px;
      font-size: 18px;
      font-weight: normal;
    }
    .function-name.user-function {
      font-weight: 300;
    }
    .employee-name {
      display: inline-block;
      font-weight: 300;
      font-size: 18px;
    }
    .employee-name.has-contact-info {
      display: inline-block;
      color: inherit;
      border-bottom: 1px solid black;
      cursor: pointer;
    }
    .task {
      position: relative;
      margin-top: 8px;
      border-radius: 20px;
      padding: 8px 10px;
      background: white;
      transition: all 0.3s;
    }
    .task.open {
      padding: 12px 16px;
    }
    .task-name {
      font-size: 17px;
      line-height: 140%;
      font-weight: normal;
      transition: all 0.3s;
    }
    .task.open .task-name {
      font-size: 20px;
      font-weight: 500;
      transition: all 0.3s;
    }
    .procedure-trigger {
      border-bottom: 1px solid black;
      white-space: nowrap;
      font-size: 17px;
      cursor: pointer;
      transition: font-size 0.3s;
    }
    .task.open .procedure-trigger {
      font-size: 0;
    }
    d-view-html {
      margin-top: -10px;
    }
  `;

  @property({ type: Boolean })
  isAppPlatform = false;
  @property({ type: Object })
  card!: EmergencyMeasuresCard;
  @property({ type: Boolean, reflect: true })
  empty = false;
  @property({ type: Array })
  openTasks: string[] = [];

  private isTaskOpen(uuid: string): boolean {
    return this.openTasks.includes(uuid);
  }

  private toggleTaskOpen(uuid: string) {
    if (this.openTasks.includes(uuid)) {
      this.openTasks = this.openTasks.filter((t) => t !== uuid);
    } else {
      this.openTasks = [...this.openTasks, uuid];
    }
    localStorage.setItem('emergencyMeasuresCardOpenTasks', JSON.stringify(this.openTasks));
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    const openTasks = localStorage.getItem('emergencyMeasuresCardOpenTasks');
    if (openTasks) {
      try {
        this.openTasks = JSON.parse(openTasks);
      } catch {
        localStorage.removeItem('emergencyMeasuresCardOpenTasks');
      }
    }
  }

  private async openContactDialog(person: NameEntry) {
    if (person.phone || person.email) {
      await ContactDialog.open({
        name: person.name,
        phone: person.phone,
        email: person.email,
        styling: 'emergency',
        isAppPlatform: this.isAppPlatform,
      });
    }
  }

  private renderTask(task: EmergencyTask) {
    return html`
      <div class="task${this.isTaskOpen(task.uuid) ? ' open' : ''}">
        <div class="task-name">
          ${task.name}
          ${task.content
            ? html`
                <span class="procedure-trigger" @click=${() => this.toggleTaskOpen(task.uuid)}
                  >${t('screens.readMore')}</span
                >
              `
            : nothing}
        </div>
        <d-closer @click=${() => this.toggleTaskOpen(task.uuid)}></d-closer>
        ${task.content
          ? html`
              <d-smooth-resize>
                ${this.isTaskOpen(task.uuid)
                  ? html`<d-view-html external-link-handler .value=${task.content ?? ''}></d-view-html>`
                  : nothing}
              </d-smooth-resize>
            `
          : nothing}
      </div>
    `;
  }

  private renderEmployee(employee: NameEntry) {
    return html`<span
      class="employee-name${employee.phone || employee.email ? ' has-contact-info' : ''}"
      @click=${() => this.openContactDialog(employee)}
      >${employee.name}</span
    > `;
  }

  private functionName(f: EmergencyFunctionWithTasks, userFunction: boolean) {
    if (f.mainTemplateId === functionIds.common) {
      return t('emergency.commonResponsibility');
    }
    if (userFunction) {
      let result = t('emergency.asFunction', { functionName: f.name.toLowerCase() });
      if (f.employees.length) {
        result += t('emergency.togetherWith');
      }
      return result;
    }
    return f.name;
  }

  private renderFunction(func: EmergencyFunctionWithTasks, userFunction: boolean) {
    return html`
      <div class="function">
        <div class="function-header">
          <div class="function-name ${userFunction ? 'user-function' : ''}">
            ${this.functionName(func, userFunction)}
          </div>
          ${func.mainTemplateId === functionIds.common
            ? nothing
            : html`
                ${func.employees.map((e) => {
                  return html`${this.renderEmployee(e)}`;
                })}
              `}
        </div>
        <div class="tasks">
          ${func.tasks.map((task) => {
            return html`${this.renderTask(task)}`;
          })}
        </div>
      </div>
    `;
  }

  private renderFunctions(situation: EmergencyMeasuresCard) {
    return html`
      <div class="functions user-functions">
        <div class="tasks-header user-tasks${situation.userFunctions.length === 0 ? ' no-tasks' : ''}">
          ${t('emergency.myTasks')}
        </div>
        ${situation.userFunctions.map((f) => {
          return html`${this.renderFunction(f, true)}`;
        })}
      </div>
      <div class="functions other-functions">
        <div class="tasks-header other-tasks${situation.otherFunctions.length === 0 ? ' no-tasks' : ''}">
          <span>${t('emergency.othersTasks')}</span>
        </div>
        ${situation.otherFunctions.map((f) => {
          return html`${this.renderFunction(f, false)}`;
        })}
      </div>
    `;
  }

  render() {
    return html` <d-smooth-resize> ${this.renderFunctions(this.card)}</d-smooth-resize>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-emergency-measures-card': DEmergencyMeasuresCard;
  }
}
